/* Slide-ready PNG helpers for the Mandate checks panel.

   Render an inline-styled HTML fragment to a PNG (SVG foreignObject → canvas, rasterised at
   2×), stamp the PNG with a physical DPI so PowerPoint / Word size it to a chosen width on
   paste, and put it on the clipboard as an image (download fallback when the browser
   cannot write images to the clipboard). Self-contained: nothing here touches the
   Charts & tables exporter in ChangesReport.jsx. */

/* HTML → { blob, width, height }. Everything is inline / same-document so the canvas stays
   untainted. Locally installed fonts (Calibre) render; web fonts would not. */
export async function htmlToPngBlob(html, { scale = 2 } = {}) {
  const holder = document.createElement('div');
  holder.setAttribute('style', 'display:inline-block;background:#ffffff;');
  holder.innerHTML = html;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;left:-9999px;top:0;';
  probe.appendChild(holder);
  document.body.appendChild(probe);
  const w = Math.ceil(holder.offsetWidth);
  const h = Math.ceil(holder.offsetHeight);
  const xhtml = new XMLSerializer().serializeToString(holder);
  document.body.removeChild(probe);
  if (!w || !h) throw new Error('Nothing to render — the table came out empty');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">`
    + `<foreignObject width="100%" height="100%">${xhtml}</foreignObject></svg>`;
  const img = new Image();
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = () => rej(new Error('Could not render the table to an image'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
  if (!blob) throw new Error('PNG encoding failed');
  return { blob, width: w, height: h };
}

/* PNG pHYs chunk: Office reads it to pick the insert size, so a 2× bitmap stamped at 192 DPI
   lands at exactly its CSS width in inches rather than twice that. Any existing pHYs chunk
   is dropped first so there is only ever one. */
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[n] = c >>> 0;
  }
  return t;
})();
const crc32 = (bytes) => {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
};

export async function withPngDpi(blob, dpi) {
  const src = new Uint8Array(await blob.arrayBuffer());
  const ppm = Math.round(dpi / 0.0254);                 // pixels per metre
  const phys = new Uint8Array(4 + 4 + 9 + 4);
  const dv = new DataView(phys.buffer);
  dv.setUint32(0, 9);
  phys.set([0x70, 0x48, 0x59, 0x73], 4);                // 'pHYs'
  dv.setUint32(8, ppm);
  dv.setUint32(12, ppm);
  phys[16] = 1;                                         // unit = metre
  dv.setUint32(17, crc32(phys.subarray(4, 17)));

  // Walk the chunks: keep everything except an existing pHYs; insert ours right after IHDR.
  const parts = [src.subarray(0, 8)];                   // signature
  let pos = 8;
  while (pos + 8 <= src.length) {
    const len = new DataView(src.buffer, src.byteOffset + pos, 4).getUint32(0);
    const type = String.fromCharCode(src[pos + 4], src[pos + 5], src[pos + 6], src[pos + 7]);
    const end = pos + 12 + len;
    if (type !== 'pHYs') parts.push(src.subarray(pos, end));
    if (type === 'IHDR') parts.push(phys);
    pos = end;
  }
  return new Blob(parts, { type: 'image/png' });
}

/* Image → clipboard; falls back to a download (returns 'clipboard' | 'download'). */
export async function copyPngToClipboard(blob, filename) {
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      return 'clipboard';
    }
  } catch (e) { /* fall through to download */ }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  return 'download';
}
